import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { clientsApi } from '../../api/facades';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const ClientCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [company_name, setCompanyName] = useState('');
  const [client_type, setClientType] = useState<'single' | 'multiple'>('multiple');
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [main_contact_position, setMainContactPosition] = useState('');
  const [mobile, setMobile] = useState('');
  const [company_address, setCompanyAddress] = useState('');
  const [address, setAddress] = useState('');
  const [reference_name, setReferenceName] = useState('');
  const [reference_mobile, setReferenceMobile] = useState('');

  const create = useMutation({
    mutationFn: () =>
      clientsApi.create({
        name: name.trim() || company_name.trim() || [first_name, last_name].filter(Boolean).join(' ').trim() || 'Unnamed',
        company_name: company_name.trim() || null,
        client_type,
        first_name: first_name.trim() || undefined,
        last_name: last_name.trim() || null,
        email: email.trim() || null,
        mobile: mobile.trim() || null,
        address: address.trim() || company_address.trim() || null,
        main_contact_position: main_contact_position.trim() || null,
        company_address: company_address.trim() || null,
        reference_name: reference_name.trim() || null,
        reference_mobile: reference_mobile.trim() || null,
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id) navigate(`/dashboard/clients/${id}`);
      else navigate('/dashboard/clients');
      toast.success('Client created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const canSubmit = name.trim() || company_name.trim() || first_name.trim();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Create a new client. Company name or main contact name is required.</p>

      <form onSubmit={onSubmit} className="space-y-8">
        <section>
          {/* <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Company / Name</h2> */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormField label="Company name" hint="Primary for corporate clients">
                <input className={inputClass} value={company_name} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g. Chikale Foods Company Limited" />
              </FormField>
            </div>
            <FormField label="Display name" hint="Fallback if no company name">
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Corp" />
            </FormField>
            <FormField label="Client type" hint="Required">
              <select className={inputClass} value={client_type} onChange={(e) => setClientType(e.target.value as 'single' | 'multiple')} required>
                <option value="single">Individual</option>
                <option value="multiple">Corporate client</option>
              </select>
            </FormField>
          </div>
        </section>

        <section>
          <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Main contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="First name">
              <input className={inputClass} value={first_name} onChange={(e) => setFirstName(e.target.value)} placeholder="Optional" />
            </FormField>
            <FormField label="Last name">
              <input className={inputClass} value={last_name} onChange={(e) => setLastName(e.target.value)} placeholder="Optional" />
            </FormField>
            <FormField label="Email">
              <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@example.com" />
            </FormField>
            <FormField label="Position">
              <input className={inputClass} value={main_contact_position} onChange={(e) => setMainContactPosition(e.target.value)} placeholder="e.g. Head of Legal" />
            </FormField>
            <div className="md:col-span-2">
              <FormField label="Mobile">
                <input className={inputClass} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+265 999 123 456" />
              </FormField>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Company address</h2>
          <FormField label="Address">
            <textarea className={inputClass + ' min-h-[80px]'} value={company_address} onChange={(e) => setCompanyAddress(e.target.value)} placeholder="Full address (street, area, P.O. Box, city, country)" rows={3} />
          </FormField>
          <FormField label="Address (contact)" hint="Optional; stored on main contact">
            <input className={inputClass} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Optional" />
          </FormField>
        </section>

        <section>
          <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Other</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Reference name">
              <input className={inputClass} value={reference_name} onChange={(e) => setReferenceName(e.target.value)} placeholder="Optional" />
            </FormField>
            <FormField label="Reference mobile">
              <input className={inputClass} value={reference_mobile} onChange={(e) => setReferenceMobile(e.target.value)} placeholder="Optional" />
            </FormField>
          </div>
        </section>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!canSubmit || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default ClientCreatePage;
